import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, Eye, Star } from "lucide-react";
import { adminListVehicles, adminDeleteVehicle, adminUpdateVehicle, resolveImageUrl } from "../lib/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    adminListVehicles()
      .then(setVehicles)
      .catch(() => toast.error("Failed to load vehicles"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    try {
      await adminDeleteVehicle(id);
      toast.success("Vehicle removed");
      setConfirmDeleteId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleFeatured = async (v) => {
    try {
      await adminUpdateVehicle(v.id, { featured: !v.featured });
      toast.success(v.featured ? "Removed from featured" : "Marked as featured");
      load();
    } catch { toast.error("Update failed"); }
  };

  const toggleSold = async (v) => {
    try {
      await adminUpdateVehicle(v.id, { sold: !v.sold });
      toast.success(v.sold ? "Marked available" : "Marked sold");
      load();
    } catch { toast.error("Update failed"); }
  };

  const money = (n) => `$${(n || 0).toLocaleString()}`;

  return (
    <div data-testid="page-admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Inventory</h1>
            <p className="text-white/50 mt-3 text-sm">Add, edit, feature, or mark cars sold.</p>
          </div>
          <Link to="/admin/vehicles/new" data-testid="admin-new-vehicle" className="btn-primary">
            <Plus size={16} /> New Vehicle
          </Link>
        </div>
      </motion.div>

      <div className="mt-8 glass rounded-2xl overflow-hidden" data-testid="admin-vehicles-table">
        <div className="grid grid-cols-[80px_1fr_120px_100px_100px_180px] gap-4 px-6 py-4 border-b border-white/10 text-xs uppercase tracking-widest text-white/40 hidden md:grid">
          <div>Image</div>
          <div>Vehicle</div>
          <div>Price</div>
          <div>Featured</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-white/40">Loading...</div>
        ) : vehicles.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-white/60">No vehicles yet.</div>
            <Link to="/admin/vehicles/new" className="btn-primary mt-6 inline-flex">
              <Plus size={16} /> Add your first vehicle
            </Link>
          </div>
        ) : (
          vehicles.map((v) => (
            <div key={v.id} data-testid={`admin-vehicle-row-${v.id}`}
              className="grid grid-cols-1 md:grid-cols-[80px_1fr_120px_100px_100px_180px] gap-4 px-6 py-4 border-b border-white/5 items-center hover:bg-white/[0.02] transition-colors">
              <div className="w-20 h-14 rounded overflow-hidden bg-white/5">
                {v.images?.[0] && (
                  <img src={resolveImageUrl(v.images[0])} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-display text-xl tracking-tight">{v.year} {v.make} {v.model}</div>
                <div className="text-xs text-white/40 mt-1">VIN {v.vin} · {v.mileage?.toLocaleString()} mi</div>
              </div>
              <div className="font-display text-xl">{money(v.price)}</div>
              <button data-testid={`toggle-featured-${v.id}`} onClick={() => toggleFeatured(v)}
                className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${v.featured ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-white/10 text-white/40 hover:border-white/30"}`}>
                <Star size={14} fill={v.featured ? "currentColor" : "none"} />
              </button>
              <button data-testid={`toggle-sold-${v.id}`} onClick={() => toggleSold(v)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${v.sold ? "border-white/10 text-white/40" : "border-[var(--primary)]/40 text-[var(--primary)]"}`}>
                {v.sold ? "SOLD" : "For Sale"}
              </button>
              <div className="flex gap-2 md:justify-end">
                <Link to={`/vehicle/${v.id}`} target="_blank" className="w-9 h-9 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center" aria-label="View">
                  <Eye size={14} />
                </Link>
                <Link to={`/admin/vehicles/edit/${v.id}`} data-testid={`edit-vehicle-${v.id}`}
                  className="w-9 h-9 rounded-full border border-white/10 hover:border-[var(--primary)] hover:text-[var(--primary)] flex items-center justify-center" aria-label="Edit">
                  <Pencil size={14} />
                </Link>
                {confirmDeleteId === v.id ? (
                  <>
                    <button data-testid={`confirm-delete-${v.id}`} onClick={() => onDelete(v.id)}
                      className="text-xs px-3 py-1.5 rounded-full bg-[var(--primary)] text-white">Confirm</button>
                    <button onClick={() => setConfirmDeleteId(null)}
                      className="text-xs px-3 py-1.5 rounded-full border border-white/10">Cancel</button>
                  </>
                ) : (
                  <button data-testid={`delete-vehicle-${v.id}`} onClick={() => setConfirmDeleteId(v.id)}
                    className="w-9 h-9 rounded-full border border-white/10 hover:border-red-500 hover:text-red-500 flex items-center justify-center" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
